import axios from 'axios'
import * as O from 'fp-ts/lib/Option'
import * as E from 'fp-ts/lib/Either'
import { pipe } from 'fp-ts/lib/pipeable'
import { v4 as uuid4 } from 'uuid'

import { AttachmentApi } from './attachment-api'
import { AttachmentError } from './attachment-error'
import { AttachmentEvent } from './attachment-event'
import { AttachmentSupport, generateUniqueName } from './attachment-support'
import {
  AttachmentMetadata,
  ErroredAttachment,
  LoadingAttachment,
  UploadedAttachment
} from './model'

export type ResourcePathProvider = (filename: string) => string

export interface AttachmentService {
  addAttachment(
    file: File,
    takenFilenames: string[],
    resourcePathProvider: ResourcePathProvider,
    onProgress: (attachmentEvent: AttachmentEvent.Type) => Promise<void>
  ): Promise<any>

  deleteAttachment(model: LoadingAttachment): Promise<any>
}

export default function (api: AttachmentApi): AttachmentService {
  return {
    addAttachment,
    deleteAttachment
  }

  async function addAttachment(
    file: File,
    takenFilenames: string[],
    resourcePathProvider: ResourcePathProvider,
    onProgress: (attachmentEvent: AttachmentEvent.Type) => Promise<void>
  ) {
    const uniqFilename = generateUniqueName(takenFilenames, file.name)
    const filePath = resourcePathProvider(uniqFilename)
    const attachment: AttachmentMetadata = {
      name: uniqFilename,
      path: filePath,
      version: O.none
    }
    const modelId = uuid4()

    const maybeError = await AttachmentSupport.checkIfSupported(file)
    if (E.isLeft(maybeError)) {
      const model = new ErroredAttachment(modelId, attachment, maybeError.left)
      return onProgress({ model, kind: 'InitError' })
    }

    const validFile = maybeError.right
    const cancelToken = axios.CancelToken.source()

    try {
      await onProgress({
        kind: 'Init',
        model: new LoadingAttachment(
          modelId,
          attachment,
          validFile.file,
          cancelToken,
          0
        )
      })

      const response = await api.upload(
        filePath,
        validFile.file,
        validFile.mimeType,
        cancelToken,
        (event) =>
          onProgress({
            kind: 'Progress',
            model: new LoadingAttachment(
              modelId,
              attachment,
              validFile.file,
              cancelToken,
              Math.round((event.loaded / (event.total || 1)) * 100)
            )
          })
      )

      return onProgress(
        pipe(
          response.version,
          O.fold<string, AttachmentEvent.Type>(
            () => ({
              kind: 'LoadError',
              model: new ErroredAttachment(
                modelId,
                attachment,
                new AttachmentError.MissingVersion(validFile.file)
              )
            }),
            () => ({
              kind: 'Done',
              model: new UploadedAttachment(modelId, {
                ...attachment,
                version: response.version
              })
            })
          )
        )
      )
    } catch (e) {
      return onProgress({
        kind: 'LoadError',
        model: new ErroredAttachment(
          modelId,
          attachment,
          new AttachmentError.Unknown(validFile.file, e)
        )
      })
    }
  }

  async function deleteAttachment(model: LoadingAttachment) {
    if (model.kind === 'LoadingAttachment') {
      return model.cancelToken.cancel()
    }
  }
}
